import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { getCurrentLevel, getXPProgress, getCurrentTier, tiers } from '../../constants/levels';
import { AVAILABLE_LANGUAGES, GAME_GENRES, PLATFORMS } from '../../constants/profile';
import useProfileEdit from '../../hooks/useProfileEdit';
import InlineEditSection from '../shared/InlineEditSection';

export default function ProfilePage({ userData = {}, stats = {} }) {
  const { currentUser } = useAuth();
  const { questsCompleted = 0, questsAbandoned = 0, totalXP = 0 } = stats;

  const currentLevel = getCurrentLevel(totalXP);
  const xpProgress = getXPProgress(totalXP);
  const currentTier = getCurrentTier(totalXP);
  const tierData = tiers[currentTier];

  const completionRate = questsCompleted + questsAbandoned > 0
    ? Math.round((questsCompleted / (questsCompleted + questsAbandoned)) * 100) : 0;

  const username = userData.username || currentUser?.displayName || 'Player';
  const initials = username.charAt(0).toUpperCase();

  const {
    editingSection, startEditing, cancelEditing, saveSection,
    editBio, setEditBio,
    editSocialLinks, setEditSocialLinks,
    editStreamingLanguages,
    editGenres, setEditGenres,
    editPlatforms, setEditPlatforms,
    editRegion, setEditRegion,
    editMatureContent, setEditMatureContent,
    handlePrimaryLanguageChange, handleSecondaryLanguageChange,
    removeSecondaryLanguage, getAvailableLanguages, getLanguageName,
    isSocialLinksValid,
    continentOptions, countriesByContinent, isCountriesLoaded, countriesLoadError,
    isBioEmpty, isSocialEmpty, isLanguagesEmpty, isGenresEmpty,
    isPlatformsEmpty, isRegionEmpty,
  } = useProfileEdit(userData);

  return (
    <div className="profile-page" style={{ '--tier-color': tierData.color, '--tier-color-glow': tierData.color + '40' }}>

      {/* Hero Section — avatar + info + XP bar + stats */}
      <div className="profile-page-hero" style={{
        '--tier-color-mid': tierData.color + '70',
        '--tier-color-bg': tierData.color + '08'
      }}>
        <div className="profile-page-hero-accent" />

        {/* Top row: avatar + identity */}
        <div className="profile-page-hero-top">
          <div className="profile-page-avatar">
            {userData.profileImageUrl ? (
              <img src={userData.profileImageUrl} alt="" className="profile-page-avatar-img" />
            ) : (
              <span className="profile-page-avatar-initials">{initials}</span>
            )}
          </div>
          <div className="profile-page-hero-info">
            <h1 className="profile-page-username">{username}</h1>
            <p className="profile-page-email">{currentUser?.email}</p>

            {/* Social links */}
            <div className="profile-page-social">
              {editingSection !== 'social' && (
                isSocialEmpty ? (
                  <button className="sp-add-social-btn" onClick={() => startEditing('social')}>
                    <FontAwesomeIcon icon={ICONS.PLUS} />
                    <span>Add social links</span>
                  </button>
                ) : (
                  <div className="sp-social-row">
                    <div className="sp-social-icons">
                      {userData.socialLinks?.twitch && (
                        <a href={`https://twitch.tv/${userData.socialLinks.twitch}`}
                          target="_blank" rel="noopener noreferrer"
                          className="social-icon-link twitch" title={userData.socialLinks.twitch}>
                          <FontAwesomeIcon icon={ICONS.TWITCH} />
                        </a>
                      )}
                      {userData.socialLinks?.customLink?.link && (
                        <a href={userData.socialLinks.customLink.link}
                          target="_blank" rel="noopener noreferrer"
                          className="social-icon-link custom-link"
                          title={userData.socialLinks.customLink.name || 'Link'}>
                          <FontAwesomeIcon icon={ICONS.EXTERNAL_LINK} />
                        </a>
                      )}
                    </div>
                    <button className="inline-edit-btn-mini edit"
                      onClick={() => startEditing('social')} title="Edit social links">
                      <FontAwesomeIcon icon={ICONS.EDIT} />
                    </button>
                  </div>
                )
              )}
              {editingSection === 'social' && (
                <div className="sp-social-edit" onClick={(e) => e.stopPropagation()}>
                  <div className="social-edit-inputs-inline">
                    <div className="social-input-group-inline">
                      <FontAwesomeIcon icon={ICONS.TWITCH} className="social-icon-inline" />
                      <input type="text" value={editSocialLinks.twitch}
                        onChange={(e) => setEditSocialLinks({ ...editSocialLinks, twitch: e.target.value })}
                        placeholder="Twitch username" className="social-input-inline" />
                    </div>
                    <div className="social-input-group-inline">
                      <FontAwesomeIcon icon={ICONS.LINK} className="social-icon-inline" />
                      <input type="text" value={editSocialLinks.customLink?.name || ''}
                        onChange={(e) => setEditSocialLinks({ ...editSocialLinks, customLink: { ...editSocialLinks.customLink, name: e.target.value } })}
                        placeholder="Link name" className="social-input-inline" />
                      <input type="text" value={editSocialLinks.customLink?.link || ''}
                        onChange={(e) => setEditSocialLinks({ ...editSocialLinks, customLink: { ...editSocialLinks.customLink, link: e.target.value } })}
                        placeholder="https://..." className="social-input-inline" />
                    </div>
                  </div>
                  <div className="inline-edit-actions-mini">
                    <button onClick={() => saveSection('social', { socialLinks: editSocialLinks })}
                      className="inline-save-btn" disabled={!isSocialLinksValid()}>
                      <FontAwesomeIcon icon={ICONS.CHECK} />
                    </button>
                    <button onClick={cancelEditing} className="inline-cancel-btn">
                      <FontAwesomeIcon icon={ICONS.TIMES} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* XP bar header row */}
        <div className="xp-bar-epic-container">
          <div className="xp-bar-header">
            <div className="level-display-epic">
              <span className="level-label-epic">LEVEL</span>
              <span className="level-number-epic">{currentLevel}</span>
            </div>
            <div className="tier-display-epic">
              <span className="tier-label-epic">TIER</span>
              <span className="tier-name-epic" style={{ color: tierData.color }}>{tierData.name}</span>
            </div>
            <div className="total-xp-display">
              <span className="total-xp-label">TOTAL XP</span>
              <span className="total-xp-value" style={{ color: tierData.color }}>{totalXP.toLocaleString()}</span>
            </div>
          </div>

          <div className="xp-bar-epic">
            <div className="xp-bar-background">
              <div className="xp-bar-glow-bg" style={{ backgroundColor: tierData.color + '20' }}></div>
            </div>
            <div className="xp-bar-fill-epic" style={{
              width: `${currentLevel === 50 ? 100 : xpProgress.percentage}%`,
              background: `linear-gradient(90deg, ${tierData.color}99, ${tierData.color})`,
              boxShadow: `0 0 20px ${tierData.color}60, inset 0 2px 4px rgba(255,255,255,0.3)`
            }}>
              <div className="xp-bar-shine"></div>
              <div className="xp-bar-texture"></div>
            </div>
            <div className="xp-progress-overlay">
              {currentLevel === 50 ? (
                <span className="max-level-text">MAX LEVEL REACHED</span>
              ) : (
                <span className="xp-progress-text">{xpProgress.current} / {xpProgress.needed} XP</span>
              )}
            </div>
          </div>

          <div className="xp-stats-footer">
            <div className="quest-stats">
              <div className="stat-item-epic">
                <FontAwesomeIcon icon={ICONS.PERCENT} className="stat-icon-epic" />
                <div className="stat-content-epic">
                  <span className="stat-number-epic">{completionRate}%</span>
                  <span className="stat-label-epic">Success</span>
                </div>
              </div>
              <div className="stat-item-epic">
                <FontAwesomeIcon icon={ICONS.TROPHY} className="stat-icon-epic" />
                <div className="stat-content-epic">
                  <span className="stat-number-epic">{questsCompleted}</span>
                  <span className="stat-label-epic">Completed</span>
                </div>
              </div>
              <div className="stat-item-epic">
                <FontAwesomeIcon icon={ICONS.FIRE} className="stat-icon-epic" />
                <div className="stat-content-epic">
                  <span className="stat-number-epic">{questsAbandoned}</span>
                  <span className="stat-label-epic">Abandoned</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section */}
      <div className="profile-page-bio">
        <div className="profile-page-section-header">
          <FontAwesomeIcon icon={ICONS.EDIT} className="profile-page-section-icon" />
          <h3>About</h3>
        </div>
        {editingSection === 'bio' ? (
          <div className="profile-page-bio-edit" onClick={(e) => e.stopPropagation()}>
            <textarea
              value={editBio}
              onChange={(e) => setEditBio(e.target.value)}
              placeholder="Tell us about yourself..."
              rows="4"
              maxLength="500"
              autoFocus
              className="profile-page-bio-textarea"
            />
            <div className="profile-page-bio-footer">
              <span className="profile-page-char-count">{editBio.length}/500</span>
              <div className="inline-edit-actions-mini">
                <button onClick={() => saveSection('bio', { bio: editBio })} className="inline-save-btn">
                  <FontAwesomeIcon icon={ICONS.CHECK} />
                </button>
                <button onClick={cancelEditing} className="inline-cancel-btn">
                  <FontAwesomeIcon icon={ICONS.TIMES} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="profile-page-bio-display">
            <p className="profile-page-bio-text">{userData.bio || 'No bio yet...'}</p>
            <button className={`inline-edit-btn-mini ${isBioEmpty ? 'add' : 'edit'}`}
              onClick={() => startEditing('bio')}>
              <FontAwesomeIcon icon={isBioEmpty ? ICONS.PLUS : ICONS.EDIT} />
            </button>
          </div>
        )}
      </div>

      {/* Attributes Grid */}
      <div className="profile-page-attributes">
        <InlineEditSection
          icon={ICONS.GLOBE} title="Languages"
          isEmpty={isLanguagesEmpty}
          isEditing={editingSection === 'languages'}
          onStartEdit={() => startEditing('languages')}
          onSave={() => saveSection('languages', { streamingLanguages: editStreamingLanguages })}
          onCancel={cancelEditing}
          displayContent={
            isLanguagesEmpty ? <span className="no-preferences">Not set</span> : (() => {
              const allLangs = [
                ...(userData.streamingLanguages?.primary?.[0] ? [{ code: userData.streamingLanguages.primary[0], highlight: true }] : []),
                ...(userData.streamingLanguages?.secondary || []).map(code => ({ code, highlight: false }))
              ];
              const visible = allLangs.slice(0, 2);
              const overflow = allLangs.length - 2;
              return (
                <>
                  {visible.map(l => (
                    <span key={l.code} className={`profile-badge${l.highlight ? ' highlight' : ''}`}>{getLanguageName(l.code)}</span>
                  ))}
                  {overflow > 0 && <span className="profile-badge overflow">+{overflow}</span>}
                </>
              );
            })()
          }
          editContent={
            <div className="languages-edit-inline">
              <select value={editStreamingLanguages.primary?.[0] || ''}
                onChange={(e) => handlePrimaryLanguageChange(e.target.value)} className="language-dropdown-inline">
                <option value="">Primary language</option>
                {AVAILABLE_LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
              </select>
              {editStreamingLanguages.secondary?.length > 0 && (
                <div className="selected-languages-inline">
                  {editStreamingLanguages.secondary.map(langCode => {
                    const lang = AVAILABLE_LANGUAGES.find(l => l.code === langCode);
                    return lang ? (
                      <div key={langCode} className="selected-language-item-inline">
                        <span>{lang.name}</span>
                        <button type="button" onClick={() => removeSecondaryLanguage(langCode)} className="remove-tag-btn-inline">
                          <FontAwesomeIcon icon={ICONS.TIMES} />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
              {editStreamingLanguages.secondary?.length < 5 && (
                <select value="" onChange={(e) => handleSecondaryLanguageChange(e.target.value)} className="language-dropdown-inline">
                  <option value="">+ Secondary</option>
                  {getAvailableLanguages([...(editStreamingLanguages.primary || []), ...(editStreamingLanguages.secondary || [])])
                    .map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                </select>
              )}
            </div>
          }
        />

        <InlineEditSection
          icon={ICONS.TAG} title="Game Genres"
          isEmpty={isGenresEmpty}
          isEditing={editingSection === 'genres'}
          onStartEdit={() => startEditing('genres')}
          onSave={() => saveSection('genres', { genres: editGenres })}
          onCancel={cancelEditing}
          displayContent={
            isGenresEmpty ? <span className="no-preferences">Not set</span> : (() => {
              const all = (userData.genres || []).map(code => GAME_GENRES.find(x => x.code === code)).filter(Boolean);
              const visible = all.slice(0, 2);
              const overflow = all.length - 2;
              return (
                <>
                  {visible.map(g => <span key={g.code} className="profile-badge">{g.name}</span>)}
                  {overflow > 0 && <span className="profile-badge overflow">+{overflow}</span>}
                </>
              );
            })()
          }
          editContent={
            <div className="genres-edit-inline">
              {editGenres.length > 0 && (
                <div className="selected-tags-inline">
                  {editGenres.map(code => {
                    const opt = GAME_GENRES.find(g => g.code === code);
                    return opt ? (
                      <div key={code} className="tag-item-inline">
                        <span>{opt.name}</span>
                        <button type="button" className="remove-tag-btn-inline" onClick={() => setEditGenres(editGenres.filter(c => c !== code))}>
                          <FontAwesomeIcon icon={ICONS.TIMES} />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
              {editGenres.length < 3 && (
                <select value="" onChange={(e) => {
                  const val = e.target.value;
                  if (val && !editGenres.includes(val) && editGenres.length < 3) setEditGenres([...editGenres, val]);
                }} className="preference-dropdown-inline">
                  <option value="">+ Add genre (max 3)</option>
                  {GAME_GENRES.filter(g => !editGenres.includes(g.code)).map(g => <option key={g.code} value={g.code}>{g.name}</option>)}
                </select>
              )}
            </div>
          }
        />

        <InlineEditSection
          icon={ICONS.TASKS} title="Platforms"
          isEmpty={isPlatformsEmpty}
          isEditing={editingSection === 'platforms'}
          onStartEdit={() => startEditing('platforms')}
          onSave={() => saveSection('platforms', { platforms: editPlatforms })}
          onCancel={cancelEditing}
          displayContent={
            isPlatformsEmpty ? <span className="no-preferences">Not set</span> : (() => {
              const all = (userData.platforms || []).map(code => PLATFORMS.find(x => x.code === code)).filter(Boolean);
              const visible = all.slice(0, 2);
              const overflow = all.length - 2;
              return (
                <>
                  {visible.map(p => <span key={p.code} className="profile-badge">{p.name}</span>)}
                  {overflow > 0 && <span className="profile-badge overflow">+{overflow}</span>}
                </>
              );
            })()
          }
          editContent={
            <div className="platforms-edit-inline">
              {editPlatforms.length > 0 && (
                <div className="selected-tags-inline">
                  {editPlatforms.map(code => {
                    const opt = PLATFORMS.find(p => p.code === code);
                    return opt ? (
                      <div key={code} className="tag-item-inline neutral">
                        <span>{opt.name}</span>
                        <button type="button" className="remove-tag-btn-inline" onClick={() => setEditPlatforms(editPlatforms.filter(c => c !== code))}>
                          <FontAwesomeIcon icon={ICONS.TIMES} />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
              <select value="" onChange={(e) => {
                const val = e.target.value;
                if (val && !editPlatforms.includes(val)) setEditPlatforms([...editPlatforms, val]);
              }} className="preference-dropdown-inline">
                <option value="">+ Add platform</option>
                {PLATFORMS.filter(p => !editPlatforms.includes(p.code)).map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select>
            </div>
          }
        />

        <InlineEditSection
          icon={ICONS.GLOBE} title="Region"
          isEmpty={isRegionEmpty}
          isEditing={editingSection === 'region'}
          onStartEdit={() => startEditing('region')}
          onSave={() => saveSection('region', { region: editRegion })}
          onCancel={cancelEditing}
          displayContent={
            isRegionEmpty ? <span className="no-preferences">Not set</span> : (
              <>
                <span className="profile-badge">{userData.region.continent}</span>
                {userData.region.country && <span className="profile-badge">{userData.region.country}</span>}
              </>
            )
          }
          editContent={
            <div className="region-edit-inline">
              <select className="region-dropdown-inline" value={editRegion.continent || ''}
                onChange={(e) => setEditRegion({ continent: e.target.value, country: '' })}>
                <option value="">{countriesLoadError ? 'Failed' : (isCountriesLoaded ? 'Continent' : 'Loading...')}</option>
                {isCountriesLoaded && continentOptions.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              <select className="region-dropdown-inline" value={editRegion.country || ''}
                onChange={(e) => setEditRegion({ ...editRegion, country: e.target.value })}
                disabled={!editRegion.continent || !isCountriesLoaded}>
                <option value="">Country</option>
                {(countriesByContinent[editRegion.continent] || []).map(ct => <option key={ct.code} value={ct.code}>{ct.name}</option>)}
              </select>
            </div>
          }
        />

        <InlineEditSection
          icon={ICONS.SHIELD} title="Content Rating"
          isEmpty={false}
          isEditing={editingSection === 'content'}
          onStartEdit={() => startEditing('content')}
          onSave={() => saveSection('content', { matureContent: editMatureContent })}
          onCancel={cancelEditing}
          displayContent={
            <span className={`profile-badge ${userData.matureContent === 'mature-18' ? '' : 'highlight'}`}>
              {userData.matureContent === 'mature-18' ? 'Mature 18+' : 'All Audiences'}
            </span>
          }
          editContent={
            <div className="content-edit-inline">
              <select className="content-dropdown-inline" value={editMatureContent}
                onChange={(e) => setEditMatureContent(e.target.value)}>
                <option value="all-audiences">All Audiences</option>
                <option value="mature-18">Mature 18+</option>
              </select>
            </div>
          }
        />
      </div>

    </div>
  );
}
